/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Printer, ArrowLeft } from "lucide-react";

const DOMAINS = [
  { key: "housing", label: "Housing", color: "#2B4522" },
  { key: "whanau", label: "Whānau", color: "#8B3A33" },
  { key: "justice", label: "Justice", color: "#4A5568" },
  { key: "finances", label: "Pūtea", color: "#D4A373" },
  { key: "identity", label: "Identity", color: "#9C6644" },
  { key: "community", label: "Community", color: "#6B705C" },
  { key: "education", label: "Education", color: "#B5838D" },
];

const SURVEY_TITLES = {
  housing: "Whare · Housing",
  whanau: "Whānau · Parental alienation & connection",
  reintegration: "Te ara hoki · Reintegration & decompression",
  addiction: "Hauora · Addiction recovery",
};

export default function Report() {
  const { user } = useAuth();
  const [wheel, setWheel] = useState(null);
  const [latestCheckin, setLatestCheckin] = useState(null);
  const [surveys, setSurveys] = useState({});
  const [whakatauki, setWhakatauki] = useState(null);

  useEffect(() => {
    api.get("/wheel/latest").then((r) => setWheel(r.data)).catch(() => {});
    api.get("/checkins?limit=1").then((r) => setLatestCheckin(r.data?.[0])).catch(() => {});
    api.get("/survey-responses/latest").then((r) => setSurveys(r.data || {})).catch(() => {});
    api.get("/whakatauki/today").then((r) => setWhakatauki(r.data)).catch(() => {});
  }, []);

  const today = new Date().toLocaleDateString("en-NZ", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="animate-fadeup">
      {/* Controls — hidden on print */}
      <div className="print:hidden flex items-center justify-between mb-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-charcoal"
          data-testid="report-back"
        >
          <ArrowLeft size={16} /> back
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="report-print"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ngahere text-muka hover:bg-ngahere/90"
        >
          <Printer size={16} /> Tāhia · print / save as PDF
        </button>
      </div>

      <div className="print:hidden bg-muka rounded-2xl p-4 sm:p-5 border border-border mb-6 text-sm text-charcoal/70">
        <span className="font-medium text-charcoal">Tō pukapuka · your report.</span>{" "}
        This report is generated on your device only. Nothing is sent anywhere unless you choose to share it.
        Tap "print" above to save as PDF, or print on paper for your kaiārahi / probation / lawyer / whaea — your call.
      </div>

      {/* The printable report */}
      <article className="bg-muka rounded-3xl p-8 sm:p-12 border border-border shadow-[0_4px_20px_rgba(42,42,42,0.05)] print:shadow-none print:border-0 print:rounded-none print:p-0 print:bg-white" data-testid="report-content">
        {/* Header */}
        <header className="mb-8 border-b border-border pb-6 print:border-charcoal/20">
          <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-2">
            Mā te mātau ki ora · pukapuka
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-charcoal mb-2">
            {user?.display_name || "Wāhine"}
          </h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span>{today}</span>
            <span>· {user?.email}</span>
          </div>
        </header>

        {/* Whakataukī */}
        {whakatauki && (
          <section className="mb-10">
            <p className="font-serif italic text-2xl text-charcoal leading-snug mb-2">
              "{whakatauki.text}"
            </p>
            <p className="text-sm text-muted-foreground">{whakatauki.translation}</p>
          </section>
        )}

        {/* Intentions */}
        {user?.intentions?.length > 0 && (
          <Section title="Ngā tūmanako · my intentions">
            <ul className="list-none space-y-1.5">
              {user.intentions.map((i, idx) => (
                <li key={idx} className="font-serif text-lg text-charcoal flex gap-3">
                  <span className="text-kokowai">·</span> {i}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Latest check-in */}
        {latestCheckin && (
          <Section title="Te Whare Tapa Whā · last check-in" subtitle={fmtDate(latestCheckin.created_at)}>
            <div className="grid grid-cols-4 gap-4">
              {[
                { k: "tinana", label: "Tinana", sub: "body" },
                { k: "hinengaro", label: "Hinengaro", sub: "mind" },
                { k: "wairua", label: "Wairua", sub: "spirit" },
                { k: "whanau", label: "Whānau", sub: "people" },
              ].map((w) => (
                <div key={w.k} className="text-center">
                  <div className="font-serif text-4xl text-ngahere">{latestCheckin[w.k]}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1">
                    {w.label} · {w.sub}
                  </div>
                </div>
              ))}
            </div>
            {latestCheckin.mood_word && (
              <p className="mt-4 italic text-charcoal/80 font-serif">
                One word for the day: <span className="text-kokowai">{latestCheckin.mood_word}</span>
              </p>
            )}
            {latestCheckin.journal && (
              <p className="mt-3 text-sm text-charcoal/80 whitespace-pre-wrap">{latestCheckin.journal}</p>
            )}
          </Section>
        )}

        {/* Reintegration Wheel */}
        {wheel && (
          <Section title="Te wīra · Reintegration Wheel" subtitle={fmtDate(wheel.created_at)}>
            <div className="grid sm:grid-cols-2 gap-6 items-center">
              <WheelChartPrint scores={wheel} />
              <div className="space-y-1.5">
                {DOMAINS.map((d) => (
                  <div key={d.key} className="flex items-center justify-between gap-3 py-1.5 border-b border-border/50 last:border-0">
                    <span className="text-sm text-charcoal" style={{ color: d.color }}>{d.label}</span>
                    <span className="font-serif text-2xl tabular-nums" style={{ color: d.color }}>
                      {wheel[d.key]}<span className="text-sm text-muted-foreground">/10</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
            {wheel.reflection && (
              <p className="mt-5 italic font-serif text-charcoal/80 bg-bone p-4 rounded-xl print:bg-transparent print:border print:border-charcoal/20">
                "{wheel.reflection}"
              </p>
            )}
          </Section>
        )}

        {/* Domain surveys */}
        {Object.keys(surveys).length > 0 && (
          <Section title="Ngā rangahau · domain inventories">
            <div className="space-y-5">
              {Object.entries(surveys).map(([id, resp]) => (
                <div key={id} className="border-l-2 border-kokowai pl-4">
                  <div className="flex items-baseline justify-between flex-wrap gap-2">
                    <h4 className="font-serif text-xl text-charcoal">{SURVEY_TITLES[id] || id}</h4>
                    <span className="font-serif text-2xl text-ngahere">
                      {resp.average}<span className="text-sm text-muted-foreground">/10 avg</span>
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mb-2">{fmtDate(resp.created_at)}</div>
                  {resp.reflection && (
                    <p className="text-sm italic text-charcoal/80 mt-2">"{resp.reflection}"</p>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Trusted contacts */}
        {user?.trusted_contacts?.length > 0 && (
          <Section title="Tāku kāhui · my people">
            <ul className="space-y-1">
              {user.trusted_contacts.map((c, i) => (
                <li key={i} className="text-charcoal">
                  <span className="font-medium">{c.name}</span>
                  {c.relationship && <span className="text-muted-foreground"> · {c.relationship}</span>}
                  <span className="text-muted-foreground"> · {c.phone}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Empty state */}
        {!wheel && !latestCheckin && Object.keys(surveys).length === 0 && (
          <div className="text-center py-12 text-muted-foreground font-serif italic">
            Nothing to print yet. Do a check-in, turn the wheel, or take an inventory first.
          </div>
        )}

        <footer className="mt-12 pt-6 border-t border-border text-xs text-muted-foreground text-center print:border-charcoal/20">
          <p className="font-serif italic mb-1">Whiria te muka aronga</p>
          <p>Generated on your device · Mā te mātau ki ora</p>
        </footer>
      </article>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="mb-10 print:break-inside-avoid">
      <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-1">{title}</div>
      {subtitle && <div className="text-xs text-muted-foreground mb-3">{subtitle}</div>}
      <div className={subtitle ? "" : "mt-3"}>{children}</div>
    </section>
  );
}

function WheelChartPrint({ scores }) {
  const size = 280, cx = size / 2, cy = size / 2, R = 110;
  const angles = DOMAINS.map((_, i) => (i / DOMAINS.length) * Math.PI * 2 - Math.PI / 2);
  const pts = DOMAINS.map((d, i) => {
    const r = (scores[d.key] / 10) * R;
    return [cx + Math.cos(angles[i]) * r, cy + Math.sin(angles[i]) * r];
  });
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[280px] mx-auto">
      {[0.25, 0.5, 0.75, 1].map((f, i) => (
        <circle key={i} cx={cx} cy={cy} r={R * f} fill="none" stroke="#E5E0D8" strokeWidth="1" />
      ))}
      {DOMAINS.map((d, i) => {
        const x = cx + Math.cos(angles[i]) * R;
        const y = cy + Math.sin(angles[i]) * R;
        return <line key={d.key} x1={cx} y1={cy} x2={x} y2={y} stroke="#E5E0D8" strokeWidth="1" />;
      })}
      <polygon
        points={pts.map((p) => p.join(",")).join(" ")}
        fill="rgba(43,69,34,0.18)"
        stroke="#2B4522"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="3" fill={DOMAINS[i].color} />
      ))}
      {DOMAINS.map((d, i) => {
        const x = cx + Math.cos(angles[i]) * (R + 18);
        const y = cy + Math.sin(angles[i]) * (R + 18);
        return (
          <text key={d.key} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
                fontFamily="Manrope" fontSize="10" fill="#2A2A2A">
            {d.label}
          </text>
        );
      })}
    </svg>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-NZ", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}